// Short-lived, in-memory cache for `calendar-list-events`.
//
// The edge call is a single 30-event fetch, but it pays Google/Graph round-trip latency every time
// (0.6–5.7s observed). The dashboard's "available to enrol" lane and the enrol wizard's picker both
// request the same data - the next 30 calendar events for a (workspace, provider). Caching that for a
// short TTL means the dashboard populates it on app open, and switching views or opening the wizard
// seconds later is an instant hit instead of another multi-second wait.
//
// Staleness is bounded by TTL_MS: the only thing that can go stale is the user adding/removing an
// event in their actual Google/Outlook calendar within the window. Enrolling a meeting does NOT need
// to invalidate this - the dashboard derives "available to enrol" as (these events) minus the
// enrolled-series ids, and those ids are always re-read fresh from the DB.

import { invokeEdgeFn } from './edge-functions';
import { perfEnabled } from './perf';

export type CalendarProviderKey = 'google' | 'microsoft';

export interface CalendarEventsResult {
  events?: any[];
}

const DEFAULT_TTL_MS = 60_000;

const cache = new Map<string, { ts: number; data: CalendarEventsResult }>();
const inflight = new Map<string, Promise<CalendarEventsResult>>();

function cacheKey(workspaceId: string, provider: CalendarProviderKey): string {
  return `${workspaceId}:${provider}`;
}

/**
 * Fetch the next ~30 calendar events for a (workspace, provider), served from a short-lived cache.
 * Concurrent callers for the same key share a single in-flight request (no thundering herd when the
 * dashboard and wizard both ask at once).
 *
 * @param opts.force  bypass the cache and refetch (still populates it for later callers)
 * @param opts.ttlMs  override the freshness window for this read (default 60s)
 */
export async function listCalendarEvents(
  workspaceId: string,
  provider: CalendarProviderKey,
  opts?: { force?: boolean; ttlMs?: number },
): Promise<CalendarEventsResult> {
  const key = cacheKey(workspaceId, provider);
  const ttl = opts?.ttlMs ?? DEFAULT_TTL_MS;

  if (!opts?.force) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < ttl) {
      // Make the saved round-trip visible alongside the other [perf] spans (it would otherwise just
      // be an absent `edge:calendar-list-events` line).
      if (perfEnabled()) {
        const ageMs = Math.round(Date.now() - hit.ts);
        // eslint-disable-next-line no-console
        console.log(`%c[perf] cache:calendar-list-events(${provider}) - hit · ${ageMs}ms old`, 'color:#1d9e75;font-weight:500');
      }
      return hit.data;
    }
    const pending = inflight.get(key);
    if (pending) {
      if (perfEnabled()) {
        // eslint-disable-next-line no-console
        console.log(`%c[perf] cache:calendar-list-events(${provider}) - deduped in-flight`, 'color:#1d9e75;font-weight:500');
      }
      return pending;
    }
  }

  const request = (async () => {
    const data = await invokeEdgeFn<CalendarEventsResult>('calendar-list-events', {
      workspace_id: workspaceId,
      provider,
    });
    cache.set(key, { ts: Date.now(), data });
    return data;
  })();

  inflight.set(key, request);
  try {
    return await request;
  } finally {
    inflight.delete(key);
  }
}

/**
 * Drop cached calendar events. Call after an action that changes which events should appear (e.g. a
 * calendar reconnect). With no args, clears everything; with a workspace, clears that workspace's
 * providers; with both, clears the single entry.
 */
export function invalidateCalendarEvents(workspaceId?: string, provider?: CalendarProviderKey): void {
  if (!workspaceId) {
    cache.clear();
    return;
  }
  if (provider) {
    cache.delete(cacheKey(workspaceId, provider));
    return;
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(`${workspaceId}:`)) cache.delete(key);
  }
}
